import * as React from "react";
import { Component } from "react";
import ModalPanel from "Panels/ModalPanel";
import Button from "Components/Buttons/Button";
import { Alert } from "@mui/lab";
import TextField from "Components/Input/TextField";
import Services from "Services/Platform/Services";
import ServiceGeneralError from "Services/Platform/ServiceGeneralError";
import { Units, Temperatureunits } from "@inductosense/typescript-fetch";

// Key for localStorage to track Microsoft linking state
export const LINK_MICROSOFT_KEY = "linkMicrosoftMode";

export interface LinkMicrosoftState {
    username: string;
    timestamp: number;
    refreshToken: string;
    tenant: string;
}

interface FirstLoginModalProps {
    shouldBeShown: boolean;
    username: string;
    currentEmail?: string;
    showMicrosoftLogin: boolean;
    onSetupComplete(): void;
    onRedirectToMicrosoft(): void;
}

interface FirstLoginModalState {
    newPassword: string;
    confirmPassword: string;
    email: string;
    isSaving: boolean;
    errorMessages: string[];
    passwordsMatch: boolean;
}

export default class FirstLoginModal extends Component<FirstLoginModalProps, FirstLoginModalState> {
    constructor(props: FirstLoginModalProps) {
        super(props);
        this.state = {
            newPassword: "",
            confirmPassword: "",
            email: props.currentEmail || "",
            isSaving: false,
            errorMessages: [],
            passwordsMatch: true
        };
    }

    render() {
        const { shouldBeShown, username, currentEmail, showMicrosoftLogin } = this.props;
        const { newPassword, confirmPassword, email, isSaving, errorMessages, passwordsMatch } = this.state;

        const needsEmail = !currentEmail;
        const emailValid = !needsEmail || (email.length > 0 && email.includes("@"));
        const passwordValid = newPassword.length >= 8
            && /[A-Z]/.test(newPassword)
            && /[a-z]/.test(newPassword)
            && /[0-9]/.test(newPassword)
            && /[^A-Za-z0-9]/.test(newPassword);
        const isValid = passwordValid && passwordsMatch && newPassword === confirmPassword && emailValid;

        return (
            <ModalPanel
                content={
                    <div style={{ minWidth: 400 }}>
                        <Alert severity="info" style={{ marginBottom: 16 }}>
                            Please set your <strong>iDART password</strong> to continue. This is separate from your Windows or Microsoft account password.
                        </Alert>

                        {errorMessages.map((error, index) =>
                            <Alert key={index} severity="error" style={{ marginBottom: 8 }}>{error}</Alert>
                        )}

                        <div style={{ marginBottom: 16 }}>
                            <p><strong>Username:</strong> {username}</p>
                        </div>

                        {needsEmail && (
                            <div style={{ marginBottom: 16 }}>
                                <label style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>Email Address</label>
                                <TextField
                                    width={350}
                                    type="email"
                                    autoComplete="email"
                                    initialValue=""
                                    isDisabled={isSaving}
                                    onValueChanged={change => this.setState({
                                        email: change.newValue || "",
                                        errorMessages: []
                                    })}
                                />
                                {email.length > 0 && !emailValid && (
                                    <p style={{ color: "red", fontSize: "0.9em", marginTop: 4 }}>
                                        Please enter a valid email address
                                    </p>
                                )}
                                {!(email.length > 0 && !emailValid) && (
                                    <p style={{ fontSize: "0.85em", color: "#666", marginTop: 4 }}>
                                        Required for account recovery and notifications.
                                    </p>
                                )}
                            </div>
                        )}

                        <div style={{ marginBottom: 16 }}>
                            <label style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>New iDART Password</label>
                            <TextField
                                width={350}
                                type="password"
                                autoComplete="new-password"
                                initialValue=""
                                isDisabled={isSaving}
                                onValueChanged={change => this.setState({
                                    newPassword: change.newValue || "",
                                    errorMessages: []
                                })}
                            />
                            {newPassword.length > 0 && !passwordValid && (
                                <p style={{ color: "red", fontSize: "0.9em", marginTop: 4 }}>
                                    Password must be at least 8 characters and include uppercase, lowercase, numbers, and special characters
                                </p>
                            )}
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <label style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>Confirm iDART Password</label>
                            <TextField
                                width={350}
                                type="password"
                                autoComplete="new-password"
                                initialValue=""
                                isDisabled={isSaving}
                                onValueChanged={change => {
                                    const confirmPwd = change.newValue || "";
                                    this.setState({
                                        confirmPassword: confirmPwd,
                                        passwordsMatch: this.state.newPassword === confirmPwd || confirmPwd === "",
                                        errorMessages: []
                                    });
                                }}
                            />
                            {!passwordsMatch && confirmPassword !== "" && (
                                <p style={{ color: "red", fontSize: "0.9em", marginTop: 4 }}>
                                    Passwords do not match
                                </p>
                            )}
                        </div>

                        <p style={{ fontSize: "0.85em", color: "#666", marginBottom: 16 }}>
                            Password must be at least 8 characters and include uppercase, lowercase, numbers, and special characters.
                            {showMicrosoftLogin && " After setting your password, you will be prompted to link your Microsoft account for single sign-on."}
                        </p>

                        <div style={{ textAlign: "center", marginTop: 20 }}>
                            <Button
                                label={isSaving ? "Saving..." : "Set iDART Password"}
                                onClick={() => this.handleSubmit()}
                                isDisabled={!isValid || isSaving}
                            />
                        </div>
                    </div>
                }
                title="iDART Account Setup"
                titleAlignment="centre"
                shouldBeShown={shouldBeShown}
            />
        );
    }

    private async handleSubmit() {
        const { username, currentEmail, showMicrosoftLogin } = this.props;
        const { newPassword, confirmPassword, email } = this.state;

        if (newPassword !== confirmPassword) {
            this.setState({ errorMessages: ["Passwords do not match"] });
            return;
        }

        this.setState({ isSaving: true, errorMessages: [] });

        try {
            // Get current user's unit settings to preserve them
            const uiSettings = Services.getUiSettings();
            const unitsMode = uiSettings.unitsMode === "metric" ? Units.Metric : Units.Imperial;
            const temperatureUnits = uiSettings.temperatureUnits === "celsius" ? Temperatureunits.Celsius : Temperatureunits.Fahrenheit;

            // Include email if user didn't have one
            const emailToSave = !currentEmail && email ? email : undefined;

            if (showMicrosoftLogin) {
                // Microsoft SSO is available - change password first, then redirect to Microsoft
                // Don't set ExternalLoginId yet - it will be set after Microsoft linking
                await Services.UsersService.updateUser(username, {
                    password: newPassword,
                    email: emailToSave,
                    unitsMode,
                    temperatureUnits
                });

                // Save state to localStorage so we can detect the return from Microsoft
                // Include refresh token and tenant so we can re-authenticate after redirect
                const refreshToken = Services.getLatestToken();
                const tenant = Services.getTenant();
                const linkState: LinkMicrosoftState = {
                    username,
                    timestamp: Date.now(),
                    refreshToken: refreshToken || "",
                    tenant: tenant || ""
                };
                localStorage.setItem(LINK_MICROSOFT_KEY, JSON.stringify(linkState));

                // Redirect to Microsoft login
                this.props.onRedirectToMicrosoft();
            } else {
                // Microsoft SSO not available - set LOCAL and complete setup
                await Services.UsersService.updateUser(username, {
                    password: newPassword,
                    email: emailToSave,
                    externalLoginId: "LOCAL",
                    unitsMode,
                    temperatureUnits
                });

                this.props.onSetupComplete();
            }
        } catch (ex) {
            if (ex instanceof ServiceGeneralError) {
                this.setState({
                    errorMessages: ex.responseErrors?.map(error => error.message) || ["Failed to update password"],
                    isSaving: false
                });
            } else {
                this.setState({
                    errorMessages: ["An unexpected error occurred"],
                    isSaving: false
                });
            }
        }
    }
}
